import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Layers,
  PenLine,
  RotateCcw,
  Search,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import './Stationaries.css';
import { useLotScope } from '../hooks/useLotScope';

const ITEMS_PER_PAGE = 10;

const LOT_TAB_OPTIONS = [
  { value: 'all', label: 'All LOTs' },
  { value: 'LOT 8 (Morogoro)', label: 'LOT 8' },
  { value: 'LOT 10 (Manyara)', label: 'LOT 10' },
];

const STATUS_LABELS = {
  ok: 'OK',
  low: 'Low',
  critical: 'Critical',
};

const SAMPLE_ITEMS = [
  {
    id: 1,
    lot: 'LOT 8 (Morogoro)',
    name: 'A4 photocopy paper',
    category: 'Paper',
    code: 'ST-PPR-A4',
    unit: 'Rims',
    stockQty: 120,
    minQty: 40,
    status: 'ok',
  },
  {
    id: 2,
    lot: 'LOT 10 (Manyara)',
    name: 'Ball pens – blue',
    category: 'Writing',
    code: 'ST-PEN-BL',
    unit: 'Pcs',
    stockQty: 260,
    minQty: 100,
    status: 'ok',
  },
];

const CATEGORY_PRICE_MAP = {
  Paper: 12000,
  Writing: 3500,
  Accessories: 8000,
  Storage: 10000,
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status || '—';
}

function formatCurrency(amount) {
  return `TZS ${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function getUnitPrice(item) {
  return CATEGORY_PRICE_MAP[item.category] || 5000;
}

function getSupplier(item) {
  if (item.lot === 'LOT 8 (Morogoro)') return 'Morogoro Office Supplies';
  if (item.lot === 'LOT 10 (Manyara)') return 'Manyara Stationery Hub';
  return 'Main Warehouse Supplier';
}

function Stationaries() {
  const [items] = useState(SAMPLE_ITEMS);
  const [searchQuery, setSearchQuery] = useState('');
  const { lotFilter, setLotFilter, lockedLotFilter } = useLotScope('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;

  const clearAllFilters = () => {
    setSearchQuery('');
    setLotFilter('all');
    setStatusFilter('all');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || lotFilter !== 'all' || statusFilter !== 'all';

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      if (lotFilter !== 'all' && item.lot && item.lot !== lotFilter) return false;
      if (statusFilter !== 'all' && item.status !== statusFilter) return false;
      if (!query) return true;
      const searchableText = [
        item.lot,
        item.name,
        item.category,
        item.code,
        item.unit,
        item.stockQty,
        item.minQty,
        statusLabel(item.status),
        getSupplier(item),
      ]
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [items, lotFilter, statusFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [lotFilter, statusFilter, searchQuery]);

  const totalFiltered = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
  const indexOfLastItem = Math.min(safePage * itemsPerPage, totalFiltered);
  const paginatedItems = useMemo(
    () => filteredItems.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [filteredItems, safePage, itemsPerPage]
  );

  const goToPage = (page) => {
    setCurrentPage(page);
    const tableWrapper = document.querySelector('.st-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (safePage > 1) goToPage(safePage - 1);
  };

  const goToNextPage = () => {
    if (safePage < totalPages) goToPage(safePage + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (safePage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (safePage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const lotCounts = useMemo(() => {
    const counts = { all: items.length };
    LOT_TAB_OPTIONS.forEach((opt) => {
      if (opt.value === 'all') return;
      counts[opt.value] = items.filter((item) => item.lot === opt.value).length;
    });
    return counts;
  }, [items]);

  const stats = useMemo(() => {
    const total = items.length;
    const ok = items.filter((item) => item.status === 'ok').length;
    const low = items.filter((item) => item.status === 'low').length;
    const critical = items.filter((item) => item.status === 'critical').length;
    const categories = new Set(items.map((item) => item.category).filter(Boolean)).size;
    const attention = low + critical;
    return {
      total,
      ok,
      categories,
      attention,
      okPct: total > 0 ? Math.round((ok / total) * 100) : 0,
      attentionPct: total > 0 ? Math.round((attention / total) * 100) : 0,
    };
  }, [items]);

  const subtotalValue = useMemo(() => {
    const total = filteredItems.reduce((sum, item) => {
      return sum + item.stockQty * getUnitPrice(item);
    }, 0);
    return formatCurrency(total);
  }, [filteredItems]);

  const handleExportToExcel = () => {
    try {
      if (items.length === 0) {
        alert('No data to export');
        return;
      }

      const exportData = items.map((item) => {
        const unitPrice = getUnitPrice(item);
        return {
          'LOT Name': item.lot || '',
          'Item Name': item.name || '',
          Category: item.category || '',
          Code: item.code || '',
          Unit: item.unit || '',
          'Qty Delivered': item.stockQty ?? 0,
          'Unit Price': unitPrice,
          'Total Amount': (item.stockQty || 0) * unitPrice,
          Supplier: getSupplier(item),
          Status: statusLabel(item.status),
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Stationaries');
      ws['!cols'] = [
        { wch: 20 },
        { wch: 24 },
        { wch: 14 },
        { wch: 14 },
        { wch: 10 },
        { wch: 14 },
        { wch: 14 },
        { wch: 16 },
        { wch: 24 },
        { wch: 12 },
      ];

      XLSX.writeFile(wb, `Stationaries_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      alert('Failed to export data. Please try again.');
    }
  };

  const summaryCards = [
    {
      id: 'total',
      label: 'Total items',
      value: stats.total,
      accent: '#3b82f6',
      icon: PenLine,
      meta: 'All items',
    },
    {
      id: 'ok',
      label: 'In stock (OK)',
      value: stats.ok,
      accent: '#10b981',
      icon: CheckCircle2,
      meta: `${stats.okPct}%`,
    },
    {
      id: 'categories',
      label: 'Categories',
      value: stats.categories,
      accent: '#0ea5e9',
      icon: Layers,
      meta: 'Active',
    },
    {
      id: 'attention',
      label: 'Low / critical',
      value: stats.attention,
      accent: '#f59e0b',
      icon: AlertTriangle,
      meta: `${stats.attentionPct}%`,
    },
  ];

  return (
    <div className="st-page">
      <div className="st-scroll">
        <div className="st-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Stationaries' },
            ]}
          />

          <section className="st-intro" aria-label="Stationaries overview">
            <div className="st-intro-text">
              <h1 className="st-intro-title">Stationaries</h1>
              <p className="st-intro-subtitle">
                Track office supplies, stock levels, unit prices, and suppliers by LOT
              </p>
            </div>
            <div className="st-intro-actions">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="st-soft-btn h-9 w-9"
                onClick={handleExportToExcel}
                disabled={items.length === 0}
                title="Download Excel"
                aria-label="Download Excel"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </section>

          <div className="st-lot-tabs" role="tablist" aria-label="Filter by LOT">
            {LOT_TAB_OPTIONS.filter(
              (option) => !lockedLotFilter || option.value === lockedLotFilter
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={lotFilter === option.value}
                className={`st-lot-tabs__btn${lotFilter === option.value ? ' is-active' : ''}`}
                onClick={() => {
                  if (!lockedLotFilter) setLotFilter(option.value);
                }}
              >
                {option.label}
                <span className="st-lot-tabs__count">{lotCounts[option.value] || 0}</span>
              </button>
            ))}
          </div>

          <div className="st-summary-cards">
            {summaryCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.id}
                  className="st-summary-card"
                  style={{ '--cat-accent': card.accent }}
                >
                  <div className="st-summary-card__top">
                    <span className="st-summary-card__icon" aria-hidden>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="st-summary-card__label">{card.label}</span>
                  </div>
                  <div className="st-summary-card__count">{card.meta}</div>
                  <div className="st-summary-card__amount">
                    {card.value.toLocaleString('en-US')}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="st-table-section__head">
            <h2>
              Stationary stock levels
              <span className="st-table-section__count">{totalFiltered}</span>
            </h2>
            {totalFiltered > 0 ? (
              <div className="st-subtotal">
                Subtotal: <strong>{subtotalValue}</strong>
              </div>
            ) : null}
          </div>

          <div className="st-toolbar">
            <div className="st-search">
              <Search className="st-search__icon h-4 w-4" aria-hidden />
              <Input
                className="st-search__input"
                placeholder="Search item, category, code, supplier…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search stationaries"
              />
            </div>
            <select
              className="st-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Status filter"
            >
              <option value="all">All status</option>
              <option value="ok">OK</option>
              <option value="low">Low</option>
              <option value="critical">Critical</option>
            </select>
            <div className="st-toolbar__spacer" aria-hidden="true" />
            <Button
              type="button"
              variant="ghost"
              className="st-soft-btn h-9 gap-1.5"
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>

          <div className="st-table-section">
            <div className="st-table-shell">
              <div className="st-table-wrapper">
                <table className="st-table">
                  <thead>
                    <tr>
                      <th>LOT</th>
                      <th>Item name</th>
                      <th>Category</th>
                      <th>Unit</th>
                      <th>Qty delivered</th>
                      <th>Unit price</th>
                      <th>Total amount</th>
                      <th>Supplier</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedItems.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="st-empty-row">
                          No stationaries found for current filters.
                        </td>
                      </tr>
                    ) : (
                      paginatedItems.map((item) => {
                        const unitPrice = getUnitPrice(item);
                        const totalAmount = item.stockQty * unitPrice;
                        return (
                          <tr key={item.id}>
                            <td>
                              <span
                                className={`st-lot-badge ${
                                  String(item.lot || '').includes('LOT 8')
                                    ? 'lot-badge-8'
                                    : String(item.lot || '').includes('LOT 10')
                                      ? 'lot-badge-10'
                                      : 'lot-badge-default'
                                }`}
                              >
                                {item.lot || '—'}
                              </span>
                            </td>
                            <td className="st-item-name">{item.name || '—'}</td>
                            <td>{item.category || '—'}</td>
                            <td>{item.unit || '—'}</td>
                            <td className="st-qty">{(item.stockQty ?? 0).toLocaleString('en-US')}</td>
                            <td className="st-value">{formatCurrency(unitPrice)}</td>
                            <td className="st-value">{formatCurrency(totalAmount)}</td>
                            <td>{getSupplier(item)}</td>
                            <td>
                              <span className={`st-status-badge st-status-${item.status}`}>
                                {statusLabel(item.status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {totalFiltered > 0 ? (
              <div className="st-pagination">
                <div className="st-pagination__info">
                  Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
                </div>
                <div className="st-pagination__controls">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="st-soft-btn h-8 w-8"
                    onClick={goToPreviousPage}
                    disabled={safePage === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="st-pagination__numbers">
                    {getPageNumbers().map((pageNum, index) =>
                      pageNum === '...' ? (
                        <span key={`ellipsis-${index}`} className="st-pagination__ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          type="button"
                          className={`st-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                          onClick={() => goToPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="st-soft-btn h-8 w-8"
                    onClick={goToNextPage}
                    disabled={safePage === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stationaries;
